import React from "react";
import {
  Activity,
  ArrowDownLeft,
  ArrowUpRight,
  Coins,
  Info,
  RefreshCw,
  Scale,
  TrendingUp,
} from "lucide-react";
import Card from "../common/Card";
import Badge from "../common/Badge";
import GlowArea from "../charts/GlowArea";
import { fmtUsd, fmtUsdSigned, fmtPriceUsd, fmtPct, fmtMonthLabel, shortAddr } from "./format";

/**
 * PerformanceSection — account reporting rendered verbatim from the engine's
 * GET /report and GET /report/history (via useEngineReport in the tab).
 * Read-only: moves no money, changes no execution.
 *
 * Honesty rules (enforced here and in reportModel.js):
 *  - "Deposited" is NET external funding (deposits minus withdrawals).
 *    Gross deposits appear only in the tile's tooltip, labeled as such.
 *    Missing net figure ⇒ "—", never the gross number in its place.
 *  - Time-weighted ROI is null until the engine has ≥2 snapshots ⇒
 *    "—" / "building history", never a fabricated number.
 *  - "Average monthly return" shows a number ONLY when the engine reports
 *    insufficientHistory === false; otherwise "—" / "building history".
 *  - The growth chart plots /report/history verbatim and needs ≥2 plottable
 *    points; below that it keeps its "building history" empty state rather
 *    than fabricating a line. Gap months in the breakdown stay absent.
 *  - Positions the engine can't price arrive with null currentPriceUsd /
 *    unrealizedPnlUsd and render "— (unpriced)".
 *  - Backend absent ⇒ an honest "not connected" state. No mock data, ever.
 */

const tileId = (label) =>
  `sa-tile-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}`;

const Tile = ({ icon: Icon, label, value, sub, tone, tooltip }) => (
  <Card hover={false} className="!p-4" title={tooltip} data-testid={tileId(label)}>
    <p className="text-caption text-arc-ink-3 flex items-center gap-1.5">
      <Icon size={13} aria-hidden="true" /> {label}
      {tooltip && <Info size={12} className="text-arc-ink-4" aria-hidden="true" />}
    </p>
    <p
      className={[
        "text-[22px] font-bold tracking-[-0.02em] tnum mt-1",
        tone === "pos" ? "text-arc-pos" : tone === "neg" ? "text-arc-neg" : "text-arc-ink",
      ].join(" ")}
    >
      {value}
    </p>
    {sub && (
      <p
        className={`text-caption tnum mt-0.5 ${
          tone === "pos" ? "text-arc-pos" : tone === "neg" ? "text-arc-neg" : "text-arc-ink-3"
        }`}
      >
        {sub}
      </p>
    )}
  </Card>
);

const pnlTone = (n) => (n == null ? undefined : n > 0 ? "pos" : n < 0 ? "neg" : undefined);

/**
 * buildFlowMarkers — from the /report/history points (verbatim), derive the
 * deposit/withdrawal markers to overlay on the growth line so a jump reads as
 * funding, not performance. Pure so the marker logic is unit-testable.
 *
 * A marker is emitted for every point whose netExternalFlowSinceLastUsd is a
 * real non-zero number (positive ⇒ deposit, negative ⇒ withdrawal). Zero,
 * null/undefined, and NaN flows produce NO marker. Fewer than two points is not
 * a plottable history, so it yields no markers (chart is unchanged/absent).
 *
 * `x` maps to the matching chart x-label so the marker lands on the same point
 * the line plots; `y` is that point's equity. tone drives the v4 token colour
 * (pos = green deposit, neg = red withdrawal).
 */
export const buildFlowMarkers = (points, xLabels = []) => {
  if (!Array.isArray(points) || points.length < 2) return [];
  const markers = [];
  points.forEach((p, i) => {
    const flow = Number(p?.netExternalFlowSinceLastUsd ?? 0);
    if (!flow || Number.isNaN(flow) || p?.equityUsd == null) return;
    const deposit = flow > 0;
    markers.push({
      key: `sa-flow-${i}`,
      x: xLabels[i],
      y: p.equityUsd,
      tone: deposit ? "pos" : "neg",
      flowUsd: flow,
      label: `${deposit ? "Deposit" : "Withdrawal"} ${fmtUsdSigned(flow)}`,
    });
  });
  return markers;
};

const EmptyCard = ({ children }) => (
  <Card hover={false}>
    <p className="text-caption text-arc-ink-3">{children}</p>
  </Card>
);

const statusTone = (status) => {
  const s = String(status || "").toLowerCase();
  if (/fill|execut|success|done|ok/.test(s)) return "pos";
  if (/fail|reject|error|revert/.test(s)) return "neg";
  return undefined;
};

const eventTime = (e) => {
  if (e.atMs != null) {
    const d = new Date(e.atMs);
    if (!isNaN(d)) {
      return d.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    }
  }
  return e.at || "—";
};

const PosField = ({ label, children }) => (
  <span className="text-arc-ink-3">
    {label} <span className="text-arc-ink-2 tnum">{children}</span>
  </span>
);

const PerformanceSection = ({
  available,
  report,
  history,
  historyError,
  loading,
  error,
  onRefresh,
}) => {
  const twrNull = report && report.timeWeightedRoiPct == null;

  // monthlyReturns rides on both /report and /report/history — same block,
  // whichever the engine delivered. Missing on both ⇒ null ⇒ "—".
  const monthly = report?.monthlyReturns || history?.monthlyReturns || null;
  // Number ONLY on an explicit false from the engine.
  const avgBuilding = !monthly || monthly.insufficientHistory !== false;

  // Growth chart: /report/history points, plotted verbatim. One point is a
  // dot, not a history — the chart needs at least two.
  const points = history?.points || [];
  const canPlot = points.length >= 2;
  const spansYears =
    canPlot &&
    new Date(points[0].timestampMs).getFullYear() !==
      new Date(points[points.length - 1].timestampMs).getFullYear();
  const chartData = points.map((p) => ({
    t: new Date(p.timestampMs).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      ...(spansYears ? { year: "2-digit" } : {}),
    }),
    equityUsd: p.equityUsd,
  }));
  const depositCount = points.filter((p) => (p.netExternalFlowSinceLastUsd ?? 0) > 0).length;
  const withdrawalCount = points.filter((p) => (p.netExternalFlowSinceLastUsd ?? 0) < 0).length;
  // Deposit/withdrawal markers on the equity line — read-only, derived from the
  // same points the line plots. Empty/short series ⇒ [] ⇒ chart unchanged.
  const flowMarkers = buildFlowMarkers(
    points,
    chartData.map((d) => d.t)
  );

  return (
    <section aria-label="Performance" className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <h3 className="text-h2 text-arc-ink">Performance</h3>
          {report && <Badge tone="accent">Your account data · GET /report</Badge>}
        </div>
        {available && (
          <button
            type="button"
            onClick={onRefresh}
            disabled={loading}
            className="inline-flex items-center gap-1.5 text-caption text-arc-ink-2 rounded-arc-sm px-2.5 py-1.5 ring-1 ring-arc-line hover:bg-arc-paper-2 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} aria-hidden="true" />
            Refresh
          </button>
        )}
      </div>

      {!available ? (
        <EmptyCard>
          Performance reporting reads from the engine&apos;s <code>/report</code>{" "}
          endpoint, which isn&apos;t connected in this environment yet. Nothing is
          simulated in the meantime — this section stays empty until real account
          data is available.
        </EmptyCard>
      ) : error ? (
        <EmptyCard>Couldn&apos;t load your report: {error}</EmptyCard>
      ) : !report && loading ? (
        <EmptyCard>Loading your report…</EmptyCard>
      ) : !report ? (
        <EmptyCard>
          No reporting data yet. Connect your wallet and enroll first — the report
          starts once the engine has your account.
        </EmptyCard>
      ) : (
        <>
          {report.ledgerComplete === false && (
            <p className="text-caption text-arc-ink-3">
              The engine reports its funding ledger as incomplete — deposit and
              withdrawal figures below may not include every flow yet.
            </p>
          )}

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Tile icon={Coins} label="Current value" value={fmtUsd(report.equityUsd)} />
            <Tile
              icon={ArrowDownLeft}
              label="Deposited (net)"
              value={fmtUsd(report.netDepositsUsd)}
              tooltip={
                report.grossDepositsUsd != null
                  ? `Net external funding: deposits minus withdrawals. Gross deposits: ${fmtUsd(
                      report.grossDepositsUsd
                    )}.`
                  : "Net external funding: deposits minus withdrawals."
              }
            />
            <Tile icon={ArrowUpRight} label="Taken out" value={fmtUsd(report.withdrawalsUsd)} />
            <Tile
              icon={Scale}
              label="Total PnL"
              value={fmtUsdSigned(report.totalPnlUsd)}
              tone={pnlTone(report.totalPnlUsd)}
            />
            <Tile
              icon={Scale}
              label="Realized PnL"
              value={fmtUsdSigned(report.realizedPnlUsd)}
              tone={pnlTone(report.realizedPnlUsd)}
            />
            <Tile
              icon={Scale}
              label="Unrealized PnL"
              value={fmtUsdSigned(report.unrealizedPnlUsd)}
              tone={pnlTone(report.unrealizedPnlUsd)}
              tooltip="Total PnL minus realized PnL, as reported by the engine."
              sub={
                report.unpricedCount > 0
                  ? `excludes ${report.unpricedCount} unpriced position${
                      report.unpricedCount === 1 ? "" : "s"
                    }`
                  : undefined
              }
            />
            <Tile
              icon={TrendingUp}
              label="ROI (simple)"
              value={fmtPct(report.simpleRoiPct, { signed: true })}
              tone={pnlTone(report.simpleRoiPct)}
            />
            <Tile
              icon={TrendingUp}
              label="Average monthly return"
              value={avgBuilding ? "—" : fmtPct(monthly.averageMonthlyReturnPct, { signed: true })}
              tone={avgBuilding ? undefined : pnlTone(monthly.averageMonthlyReturnPct)}
              sub={
                monthly?.insufficientHistory === true
                  ? "building history — needs a completed month"
                  : !avgBuilding && monthly.completedMonths != null && monthly.completedMonths > 0
                  ? `mean of ${monthly.completedMonths} completed month${
                      monthly.completedMonths === 1 ? "" : "s"
                    }`
                  : undefined
              }
              tooltip={
                monthly?.insufficientHistory === true
                  ? "Average monthly return needs at least one completed calendar month of history. It appears once the engine has one."
                  : "Mean return across completed calendar months only — partial months are excluded. Historical account data, not a projection."
              }
            />
            <Tile
              icon={TrendingUp}
              label="ROI (time-weighted)"
              value={twrNull ? "—" : fmtPct(report.timeWeightedRoiPct, { signed: true })}
              tone={twrNull ? undefined : pnlTone(report.timeWeightedRoiPct)}
              sub={twrNull ? "building history — needs more snapshots" : undefined}
              tooltip={
                twrNull
                  ? "Time-weighted return needs at least two valuation snapshots. It appears once the engine has accumulated them."
                  : undefined
              }
            />
          </div>

          <Card hover={false}>
            <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
              <p className="text-sm font-medium text-arc-ink">Portfolio growth</p>
              <span className="text-caption text-arc-ink-4">
                Historical account data — not a projection or promise of future results
              </span>
            </div>
            {canPlot ? (
              <div data-testid="sa-growth-chart">
                <GlowArea
                  data={chartData}
                  xKey="t"
                  series={[{ key: "equityUsd", name: "Account value", tone: "gold" }]}
                  height={240}
                  yTickFormatter={(v) =>
                    `$${
                      Math.abs(Number(v)) >= 1000
                        ? `${(Number(v) / 1000).toLocaleString("en-US", {
                            maximumFractionDigits: 1,
                          })}k`
                        : Number(v).toLocaleString("en-US")
                    }`
                  }
                  valueFormatter={(v) => fmtUsd(v)}
                  markers={flowMarkers}
                />
                {(depositCount > 0 || withdrawalCount > 0) && (
                  <p className="text-caption text-arc-ink-4 mt-2">
                    This window includes {depositCount} deposit
                    {depositCount === 1 ? "" : "s"} and {withdrawalCount} withdrawal
                    {withdrawalCount === 1 ? "" : "s"} — external flows move account
                    value without being trading performance.
                  </p>
                )}
              </div>
            ) : (
              <>
                {historyError && (
                  <p className="text-caption text-arc-ink-3">
                    Couldn&apos;t load your account history: {historyError}
                  </p>
                )}
                {/* <2 plottable points from /report/history — a line needs two.
                    Anything drawn from less would fabricate history. */}
                <p className="text-caption text-arc-ink-3 py-8 text-center">
                  Building history — the growth chart appears once the engine has at
                  least two valuation snapshots for your account. Nothing is simulated
                  in the meantime.
                </p>
              </>
            )}
          </Card>

          {monthly && monthly.months.length > 0 && (
            <Card hover={false}>
              <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                <p className="text-sm font-medium text-arc-ink">Monthly returns</p>
                <span className="text-caption text-arc-ink-4">
                  Completed and partial calendar months from your account history —
                  gap months are omitted, never estimated
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {monthly.months.map((m) => (
                  <div
                    key={m.month}
                    data-testid={`sa-month-${m.month}`}
                    className="flex items-center gap-2 rounded-arc-ctl ring-1 ring-arc-line px-2.5 py-1.5 text-xs"
                  >
                    <span className="text-arc-ink-3">{fmtMonthLabel(m.month)}</span>
                    <span
                      className={`tnum ${
                        m.returnPct == null
                          ? "text-arc-ink-2"
                          : m.returnPct > 0
                          ? "text-arc-pos"
                          : m.returnPct < 0
                          ? "text-arc-neg"
                          : "text-arc-ink-2"
                      }`}
                    >
                      {fmtPct(m.returnPct, { signed: true })}
                    </span>
                    {m.partial && <Badge tone="warn">partial</Badge>}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {report.positions.length > 0 && (
            <Card hover={false}>
              <p className="text-sm font-medium text-arc-ink mb-2">Position valuation</p>
              <div className="space-y-2">
                {report.positions.map((p, i) => (
                  <div
                    key={`${p.pairKey}-${i}`}
                    data-testid={`sa-position-${p.pairKey}`}
                    className="rounded-arc-ctl ring-1 ring-arc-line p-2.5 text-xs"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-arc-ink font-medium flex items-center gap-2">
                        {p.long != null && (
                          <Badge tone={p.long ? "pos" : "neg"}>
                            {p.long ? "LONG" : "SHORT"}
                          </Badge>
                        )}
                        {p.pairKey}
                        {p.leverage != null && (
                          <span className="text-arc-ink-3 tnum font-normal">{p.leverage}×</span>
                        )}
                      </span>
                      <span
                        className={`tnum ${
                          p.unrealizedPnlUsd == null
                            ? "text-arc-ink-2"
                            : p.unrealizedPnlUsd > 0
                            ? "text-arc-pos"
                            : p.unrealizedPnlUsd < 0
                            ? "text-arc-neg"
                            : "text-arc-ink-2"
                        }`}
                      >
                        {p.unrealizedPnlUsd != null ? (
                          fmtUsdSigned(p.unrealizedPnlUsd)
                        ) : (
                          <>
                            — <span className="text-arc-ink-4">(unpriced)</span>
                          </>
                        )}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 mt-2">
                      <PosField label="Collateral">{fmtUsd(p.collateralUsd)}</PosField>
                      <PosField label="Size">{fmtUsd(p.sizeUsd)}</PosField>
                      <PosField label="Entry">{fmtPriceUsd(p.entryPriceUsd)}</PosField>
                      <PosField label="Mark">{fmtPriceUsd(p.currentPriceUsd)}</PosField>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {report.recentEvents.length > 0 && (
            <Card hover={false}>
              <p className="text-sm font-medium text-arc-ink mb-2 flex items-center gap-1.5">
                <Activity size={14} aria-hidden="true" /> Recent activity
              </p>
              <div className="space-y-1.5">
                {report.recentEvents.map((e, i) => (
                  <div
                    key={`${e.txHash || e.atMs || "evt"}-${i}`}
                    data-testid="sa-recent-event"
                    className="flex items-center justify-between gap-2 flex-wrap rounded-arc-ctl ring-1 ring-arc-line px-2.5 py-2 text-xs"
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <Badge tone={statusTone(e.status)}>{e.status || "—"}</Badge>
                      <span className="text-arc-ink">{e.pair || "—"}</span>
                      <span className="text-arc-ink-3">{e.intent || "—"}</span>
                    </span>
                    <span className="flex items-center gap-3 text-arc-ink-3 tnum">
                      {e.txHash && <span title={e.txHash}>{shortAddr(e.txHash)}</span>}
                      <span>{eventTime(e)}</span>
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}
    </section>
  );
};

export default PerformanceSection;
